import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChartBox, Loading, Banner } from '@/components/OpsUI';
import { Clock, Bot, UserCheck, Percent, MoonStar, MessageCircle } from 'lucide-react';

const fmtHours = (min) => (min >= 60 ? `${Math.floor(min / 60)} h ${min % 60} min` : `${min} min`);
const weekLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
};
const TIP = { background: '#0F1620', border: '1px solid rgba(0,204,255,0.25)', borderRadius: 8, color: '#EAF2F8' };
const AX = { fill: '#A7B6C6', fontSize: 11 };
const TITLES = { tickets: 'Tickets por semana', whatsapp: 'Respuestas de WhatsApp por semana', total: 'Respuestas por semana (Tickets + WhatsApp)' };

const Total = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="p-4" data-testid={`ai-metric-${testid}`}>
    <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">{label}<Icon className="w-4 h-4 text-[hsl(var(--primary))]" /></div>
    <div className="text-2xl font-bold mt-1 tabular-nums" data-testid={`ai-metric-${testid}-value`}>{value}</div>
    {sub && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{sub}</div>}
  </Card>
);

const WeekChart = ({ rows, channel }) => (
  <Card className="p-5">
    <h3 className="text-sm font-semibold mb-3" data-testid="ai-metrics-chart-title">{TITLES[channel]}</h3>
    <ChartBox height={280} testid="ai-metrics-chart">
      <BarChart data={rows} margin={{ left: -20, right: 12, top: 8, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey="label" tick={AX} stroke="rgba(255,255,255,0.08)" />
        <YAxis allowDecimals={false} tick={AX} stroke="rgba(255,255,255,0.08)" />
        <Tooltip contentStyle={TIP} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="auto_sent" name="Auto-respondidos" stackId="a" fill="#34D399" />
        <Bar dataKey="sent" name="Aprobados por agente" stackId="a" fill="#00CCFF" />
        <Bar dataKey="discarded" name="Descartados" stackId="a" fill="#64748B" radius={channel === 'tickets' ? [4, 4, 0, 0] : 0} />
        {channel !== 'tickets' && <Bar dataKey="notices" name="Avisos fuera de horario" stackId="a" fill="#A78BFA" radius={[4, 4, 0, 0]} />}
      </BarChart>
    </ChartBox>
  </Card>
);

export const AiMetrics = () => {
  const [channel, setChannel] = useState('tickets');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    api.get('/ai/metrics', { params: { weeks: 8, channel } }).then(({ data: d }) => setData(d)).catch((e) => setError(errText(e)));
  }, [channel]);

  if (error) return <Banner kind="error" title="No se pudieron cargar las métricas" message={error} />;
  const t = data?.totals;
  const rows = (data?.weeks || []).map((w) => ({ ...w, label: weekLabel(w.week_start) }));

  return (
    <div className="space-y-4" data-testid="ai-metrics">
      <Tabs value={channel} onValueChange={setChannel}>
        <TabsList data-testid="ai-metrics-channel">
          <TabsTrigger value="tickets" data-testid="ai-metrics-channel-tickets">Tickets</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="ai-metrics-channel-whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="total" data-testid="ai-metrics-channel-total">Total</TabsTrigger>
        </TabsList>
      </Tabs>
      {!data ? <Loading /> : (
        <>
          <div className={`grid grid-cols-2 gap-4 ${channel === 'tickets' ? 'lg:grid-cols-4' : 'lg:grid-cols-3 xl:grid-cols-6'}`}>
            <Total icon={Bot} label="Auto-respondidos (8 sem.)" value={t.auto_sent} testid="auto" />
            <Total icon={UserCheck} label="Revisados por humanos" value={t.sent + t.discarded} sub={`${t.sent} aprobados · ${t.discarded} descartados`} testid="human" />
            <Total icon={Clock} label="Tiempo ahorrado" value={fmtHours(t.minutes_saved)} sub={`${data.minutes_auto} min auto · ${data.minutes_draft} min borrador`} testid="saved" />
            <Total icon={Percent} label="Tasa de auto-respuesta" value={`${Math.round(t.auto_rate * 100)}%`} sub={`${t.draft} borradores pendientes`} testid="rate" />
            {channel !== 'tickets' && <Total icon={MoonStar} label="Avisos fuera de horario" value={t.notices} testid="notices" />}
            {channel !== 'tickets' && <Total icon={MessageCircle} label="Conversaciones nuevas" value={t.conversations ?? 0} sub="WhatsApp, 8 semanas" testid="conversations" />}
          </div>
          <WeekChart rows={rows} channel={channel} />
          <Card className="p-5">
            <h3 className="text-sm font-semibold mb-3">Minutos ahorrados por semana</h3>
            <ChartBox height={200} testid="ai-metrics-saved-chart">
              <BarChart data={rows} margin={{ left: -20, right: 12, top: 8, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" tick={AX} stroke="rgba(255,255,255,0.08)" />
                <YAxis allowDecimals={false} tick={AX} stroke="rgba(255,255,255,0.08)" />
                <Tooltip contentStyle={TIP} cursor={{ fill: 'rgba(255,255,255,0.04)' }} formatter={(v) => [`${v} min`, 'Ahorrado']} />
                <Bar dataKey="minutes_saved" fill="#F5B841" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartBox>
          </Card>
        </>
      )}
    </div>
  );
};
